import { useSyncExternalStore } from 'react';

/**
 * HTS app-shell standard — appearance preference (Light / Dark / System).
 * Mirrors Mission Control's useTheme hook: the choice persists per user in
 * localStorage, "System" follows the OS live, and the `.dark` class on
 * <html> drives both the CSS custom properties (index.css) and the Fluent
 * theme selection (App.tsx).
 */

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'ss.theme';

const media: Pick<MediaQueryList, 'matches' | 'addEventListener' | 'removeEventListener'> =
  typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : { matches: false, addEventListener: () => {}, removeEventListener: () => {} }; // jsdom has no matchMedia

const listeners = new Set<() => void>();

function read(): ThemePreference {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    // storage unavailable — fall through to system
  }
  return 'system';
}

function resolve(pref: ThemePreference): ResolvedTheme {
  return pref === 'dark' || (pref === 'system' && media.matches) ? 'dark' : 'light';
}

function apply(pref: ThemePreference) {
  const dark = resolve(pref) === 'dark';
  // Dual selector approach (documented 2026-07-29 on button-align-test):
  //   - `.dark` class on <html>: drives SS's own vanilla CSS in `index.css`
  //     (and is what the FluentProvider reads to pick htsTheme vs htsDarkTheme).
  //   - `data-theme="dark"` attribute on <html>: the HTS Platform Brand-Kit
  //     canonical kit uses `[data-theme="dark"]` as the dark-mode selector, so
  //     any future import of the canonical CSS (e.g. on a marketing surface)
  //     would resolve correctly without a selector rewrite. We keep both in
  //     sync so the vanilla CSS and the Brand-Kit can coexist on the same page.
  document.documentElement.classList.toggle('dark', dark);
  document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
}

export function setThemePreference(pref: ThemePreference) {
  try {
    localStorage.setItem(STORAGE_KEY, pref);
  } catch {
    // still applies in-session
  }
  apply(pref);
  listeners.forEach((l) => l());
}

export function getThemePreference(): ThemePreference {
  return read();
}

/** React hook: current preference, kept in sync across tabs/components. */
export function useThemePreference(): ThemePreference {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    read,
  );
}

/** React hook: the effective theme after resolving "system" against the OS. */
export function useResolvedTheme(): ResolvedTheme {
  const pref = useThemePreference();
  const systemDark = useSyncExternalStore(
    (cb) => {
      media.addEventListener('change', cb);
      return () => media.removeEventListener('change', cb);
    },
    () => media.matches,
  );
  return pref === 'dark' || (pref === 'system' && systemDark) ? 'dark' : 'light';
}

/** Call once at app start (before first render): apply stored preference and follow OS changes. */
export function initThemeMode() {
  apply(read());
  media.addEventListener('change', () => apply(read()));
}
